import {
  createSlice,
  createAsyncThunk,
  SerializedError,
} from '@reduxjs/toolkit';
import { RegisterOptions } from 'react-hook-form';
import pick from 'lodash/pick';

import { RootState, AppThunkConfig } from '../../app/store';
import { ScopeSummary } from '../../types';
import { bindAPI } from '../../app/auth';
import { generatePath, route_path } from '../../app/route';
import {
  ListScopeParams,
  ListScopesResponse,
  listScopes,
  CreateScopeParams,
  CreateScopeResponse,
  createScope,
  GetScopeParams,
  GetScopeResponse,
  getScope,
  UpdateScopeParams,
  UpdateScopeResponse,
  updateScope,
  DeleteScopeParams,
  DeleteScopeResponse,
  deleteScope,
} from '../../handlers';
import { logoutAction, hasScope } from '../auth/authSlice';
import {
  setCurrentTenantId,
  selectCurrentTenantId,
} from '../user/userSlice';
import { Scope } from '../../types';
import {
  stripEmptyString,
} from '../../utils';

export interface ScopeState {
  scopes: ScopeSummary[];
  status: 'idle'|'loading'|'failed'|'success';
  error?: SerializedError;
}

const initialState: ScopeState = {
  scopes: [],
  status: 'idle',
};

export type ListScopesActionParams = ListScopeParams;
export type ListScopesActionResponse = ListScopesResponse;
export const listScopesAction = createAsyncThunk<
  ListScopesActionResponse, ListScopesActionParams, AppThunkConfig
>(
  'scope/list',
  async(params, thunkAPI) => {
    return bindAPI(listScopes, thunkAPI)(params);
  }
);

export type CreateScopeActionParams = CreateScopeParams;
export type CreateScopeActionResponse = CreateScopeResponse;
export const createScopeAction = createAsyncThunk<CreateScopeActionResponse, CreateScopeActionParams, AppThunkConfig>(
  'scope/create',
  (params, thunkAPI) => {
    return bindAPI(createScope, thunkAPI)({
      tenant_id: params.tenant_id,
      body: pick(stripEmptyString(params.body), [ 'name', 'title', 'description' ]),
    });
  }
);

export type GetScopeActionParams = GetScopeParams;
export type GetScopeActionResponse = GetScopeResponse;
export const getScopeAction = createAsyncThunk<GetScopeActionResponse, GetScopeActionParams, AppThunkConfig>(
  'scope/get',
  (params, thunkAPI) => bindAPI(getScope, thunkAPI)(params)
);

export type UpdateScopeActionParams = UpdateScopeParams;
export type UpdateScopeActionResponse = UpdateScopeResponse;
export const updateScopeAction = createAsyncThunk<UpdateScopeActionResponse, UpdateScopeActionParams, AppThunkConfig>(
  'scope/update',
  (params, thunkAPI) => {
    return bindAPI(updateScope, thunkAPI)({
      tenant_id: params.tenant_id,
      scope_name: params.scope_name,
      body: pick(stripEmptyString(params.body, { convert_to_null: true }), [
        'title', 'description'
      ]),
    });
  }
);

export type DeleteScopeActionParams = DeleteScopeParams;
export type DeleteScopeActionResponse = DeleteScopeResponse;
export const deleteScopeAction = createAsyncThunk<DeleteScopeActionResponse, DeleteScopeActionParams, AppThunkConfig>(
  'scope/delete',
  (params, thunkAPI) => bindAPI(deleteScope, thunkAPI)(params)
);

export const scopeSlice = createSlice({
  name: 'scope',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(listScopesAction.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(listScopesAction.fulfilled, (state, action) => {
        state.status = 'success';
        state.scopes = action.payload.data;
      })
      .addCase(listScopesAction.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error;
      })
      .addCase(logoutAction, (state) => initialState)
      .addCase(setCurrentTenantId, (state) => initialState);
  },
});

export const selectScopes = (state: RootState) => state.scope.scopes;
export const selectListScopesAPIStatus = (state: RootState) => state.scope.status;

const canActionByScope = (
  scope: Pick<Scope, 'scope_id'>,
  action: 'edit'|'view'|'enable'|'disable'|'delete',
  tenant_id: string,
) => {
  let [scope_tenant_id] = scope.scope_id.split('/');
  if (scope_tenant_id !== tenant_id) {
    return false;
  }
  if (action === 'edit') {
    return true;
  } else if (action === 'view') {
    return true;
  } else if (action === 'delete') {
    return true;
  }
  return false;
};

export const selectScopesAndActions = (state: RootState) => {
  const tenant_id = selectCurrentTenantId(state);
  const scopes = selectScopes(state);
  return scopes.map(scope => {
    return {
      ...scope,
      $action: {
        can_edit: hasScope(state, 'auth-portal/user_scopes.get') &&
                  hasScope(state, 'auth-portal/user_scopes.update') &&
                  canActionByScope(scope, 'edit', tenant_id),
        can_view: hasScope(state, 'auth-portal/user_scopes.get') && canActionByScope(scope, 'view', tenant_id),
        can_enable: hasScope(state, 'auth-portal/user_scopes.update') && canActionByScope(scope, 'enable', tenant_id),
        can_disable: hasScope(state, 'auth-portal/user_scopes.update') && canActionByScope(scope, 'disable', tenant_id),
        can_delete: hasScope(state, 'auth-portal/user_scopes.delete') && canActionByScope(scope, 'delete', tenant_id),
      },
    };
  });
};
export const selectCanListScope = (state: RootState) => {
  return hasScope(state, 'auth-portal/user_scopes.list');
};

export const getScopeListUrl = (tenant_id: string) => generatePath(route_path.scope_list, { tenant_id });
export const getScopeCreateUrl = (tenant_id: string) => generatePath(route_path.scope_detail, {
  tenant_id, mode: 'create',
});
export const getScopeDetailUrl = (tenant_id: string, scope_name: string, mode: 'edit'|'view') => {
  return generatePath(route_path.scope_detail, {
    tenant_id, mode,
  })+`?name=${scope_name}`;
};

export const scope_field_options: Partial<Record<keyof Scope, RegisterOptions>> = {
  name: { 
    required: 'Required',
    minLength: {
      value: 4,
      message: 'Length must be at least 4 characters long',
    },
    maxLength: {
      value: 64,
      message: 'Length must be less than or equal to 32 characters long',
    },
    pattern: {
      value: /^[a-z][a-z_.0-9]*$/,
      message: 'Fails to match the required pattern: /^[a-z][a-z_.0-9]*$/',
    },
  },
  title: { 
    required: 'Required',
    minLength: {
      value: 2,
      message: 'Length must be at least 2 characters long',
    },
    maxLength: {
      value: 64,
      message: 'Length must be less than or equal to 64 characters long',
    },
  },
  description: { 
    maxLength: {
      value: 2048,
      message: 'Length must be less than or equal to 2048 characters long',
    },
  },
};
